/**
 * Browser Push Notification Utility
 * Handles requesting permissions and sending push notifications
 */

// Check if push notifications are supported
export function isPushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

// Get current permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Request permission for push notifications
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'error';
  }
}

// Send a browser push notification
export function sendPushNotification(title, options = {}) {
  if (!isPushSupported()) return null;
  if (Notification.permission !== 'granted') return null;

  // Don't show if tab is focused (user already sees in-app notifications)
  if (document.visibilityState === 'visible') return null;

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    silent: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);

    // Handle notification click
    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) {
        options.onClick();
      }
    };

    // Auto close after timeout (default 5 seconds)
    const timeout = options.timeout || 5000;
    setTimeout(() => notification.close(), timeout);

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
}

// Notification templates for different types
export const notificationTemplates = {
  shift_filled: (data) => ({
    title: '🎉 Shift Filled!',
    body: `${data.worker_name || 'A worker'} accepted your shift${data.location ? ` at ${data.location}` : ''}`,
    tag: 'shift_filled',
    requireInteraction: true,
    timeout: 10000
  }),

  new_application: (data) => ({
    title: '📋 New Application',
    body: `${data.worker_name || 'A worker'} applied for ${data.job_title || 'your shift'}`,
    tag: 'new_application',
    timeout: 8000
  }),

  shift_cancelled: (data) => ({
    title: '⚠️ Shift Cancelled',
    body: `${data.worker_name || 'A worker'} cancelled their shift${data.job_title ? ` for ${data.job_title}` : ''}`,
    tag: 'shift_cancelled',
    requireInteraction: true,
    timeout: 10000
  }),

  shift_reminder: (data) => ({
    title: '⏰ Shift Starting Soon',
    body: `Shift ${data.job_title || ''} starts in ${data.time_until || '1 hour'}${data.location ? ` at ${data.location}` : ''}`,
    tag: 'shift_reminder',
    requireInteraction: true,
    timeout: 15000
  }),

  new_message: (data) => ({
    title: '💬 New Message',
    body: `${data.sender_name || 'Someone'}: ${data.preview || 'sent you a message'}`,
    tag: 'new_message',
    timeout: 5000
  }),

  payment_received: (data) => ({
    title: '💰 Payment Received',
    body: `$${data.amount || '0'} has been deposited to your wallet`,
    tag: 'payment_received',
    timeout: 8000
  })
};

// Send a templated notification
export function sendTemplatedNotification(type, data = {}) {
  const template = notificationTemplates[type];
  if (!template) {
    console.warn(`Unknown notification type: ${type}`);
    return null;
  }

  const options = template(data);
  return sendPushNotification(options.title, {
    body: options.body,
    tag: options.tag,
    requireInteraction: options.requireInteraction,
    timeout: options.timeout,
    data
  });
}

// Hook for React components
import { useState, useEffect, useCallback } from 'react';

export function usePushNotifications() {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [supported] = useState(isPushSupported());

  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    return result;
  }, []);

  const sendNotification = useCallback((title, options) => {
    return sendPushNotification(title, options);
  }, []);

  const sendTypedNotification = useCallback((type, data) => {
    return sendTemplatedNotification(type, data);
  }, []);

  return {
    supported,
    permission,
    requestPermission,
    sendNotification,
    sendTypedNotification
  };
}

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  sendPushNotification,
  sendTemplatedNotification
};
